type Tr = (k: string, p?: Record<string, string | number>) => string;

/** The disclosure points, in display order. `tone` selects the glyph + accent:
 *  "ok" = a protection Beacon actually provides; "limit" = an honest caveat the
 *  user should weigh. Each `key` maps to `you.safety.<key>.h` (headline) and
 *  `.d` (detail) in the catalog. The split — two protections, then five limits —
 *  is grounded in the real help-coordination crypto model (HPKE-sealed content +
 *  withheld precise location; cleartext metadata, TOFU identities, no forward
 *  secrecy, irrevocable location grant, no per-message delete). Copy must not
 *  over-promise beyond what packages/crypto + help-publish.ts implement. */
const POINTS: ReadonlyArray<{ key: string; tone: "ok" | "limit" }> = [
  { key: "e2ee", tone: "ok" },
  { key: "location", tone: "ok" },
  { key: "metadata", tone: "limit" },
  { key: "identity", tone: "limit" },
  { key: "irrevocable", tone: "limit" },
  { key: "forwardSecrecy", tone: "limit" },
  { key: "retention", tone: "limit" },
];

function el(tag: string, cls?: string, text?: string): HTMLElement {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text != null) e.textContent = text;
  return e;
}

/** "Safety & privacy" disclosure card (You tab). Plainly states what the
 *  help-coordination chat protects and — honestly — where the limits are, so a
 *  user can judge what to share. The detailed point list sits behind a disclosure
 *  to keep the tab scannable; the one-line intro stays visible. */
export function safetyDisclosure(t: Tr): HTMLElement {
  const root = el("section", "you-card you-safety");
  root.setAttribute("data-card", "safety");
  root.append(el("div", "you-card__title bx-microlabel", t("you.safety.title")));
  root.append(el("p", "you-note bx-body", t("you.safety.intro")));

  const list = el("div", "you-safety__list");
  for (const p of POINTS) {
    const item = el("div", `you-safety__item you-safety__item--${p.tone}`);
    item.setAttribute("data-safety", p.key);
    const mark = el("span", "you-safety__mark", p.tone === "ok" ? "✓" : "!");
    mark.setAttribute("aria-hidden", "true");
    const text = el("div", "you-safety__text");
    text.append(
      el("p", "you-safety__h bx-body", t(`you.safety.${p.key}.h`)),
      el("p", "you-safety__d bx-microlabel", t(`you.safety.${p.key}.d`)),
    );
    item.append(mark, text);
    list.append(item);
  }

  const details = document.createElement("details");
  details.className = "bx-advanced you-safety__details";
  const summary = el("summary", "bx-advanced__summary bx-microlabel", t("you.safety.toggle"));
  details.append(summary, list);
  root.append(details);
  return root;
}
